import { useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { rupiah, fmtDateTime } from "../lib/format";
import { exportSalesPDF, exportPurchasesPDF, exportFinancePDF, exportRecapPDF, exportDailySeriesPDF, exportProfitPDF } from "../lib/pdf";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { FileDown, FileBarChart, CalendarRange } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";

export default function Reports() {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [data, setData] = useState({});
  const [recap, setRecap] = useState(null);
  const [series, setSeries] = useState(null);
  const [profit, setProfit] = useState(null);
  const fetchSeries = async () => {
    const { data: d } = await api.get("/reports/daily-series", { params: { start, end } });
    setSeries(d);
  };
  const fetchProfit = async () => {
    const { data: d } = await api.get("/reports/profit", { params: { start, end } });
    setProfit(d);
  };

  const range = `${start} s/d ${end}`;

  const fetchReport = async (kind) => {
    const { data: d } = await api.get(`/reports/${kind}`, { params: { start, end } });
    setData((prev) => ({ ...prev, [kind]: d }));
    return d;
  };

  const fetchRecap = async () => {
    const { data: d } = await api.get("/reports/daily-recap", { params: { date: start } });
    setRecap(d);
  };

  const exp = async (kind) => {
    const d = data[kind] || await fetchReport(kind);
    if (!d.records?.length) { toast.error("Tidak ada data untuk diekspor"); return; }
    if (kind === "sales") exportSalesPDF(d, range);
    if (kind === "purchases") exportPurchasesPDF(d, range);
    if (kind === "finance") exportFinancePDF(d, range);
    toast.success("PDF diunduh");
  };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold font-display text-slate-900">Laporan & Ekspor PDF</h1>
        <p className="text-sm text-slate-500">Filter rentang tanggal & unduh laporan</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 flex items-end gap-3 flex-wrap">
          <div className="space-y-1"><Label className="flex items-center gap-1"><CalendarRange className="h-3.5 w-3.5" /> Dari Tanggal</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start-date" /></div>
          <div className="space-y-1"><Label>Sampai Tanggal</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end-date" /></div>
        </CardContent>
      </Card>

      <Tabs defaultValue="sales" onValueChange={(v) => ["sales", "purchases"].includes(v) && fetchReport(v)}>
        <TabsList>
          <TabsTrigger value="sales" data-testid="report-tab-sales">Penjualan</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="report-tab-purchases">Pembelian</TabsTrigger>
          <TabsTrigger value="profit" data-testid="report-tab-profit">Laba</TabsTrigger>
        </TabsList>

        {(
          <TabsContent value="profit" className="space-y-3" data-testid="profit-report-content">
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchProfit} data-testid="load-profit-report-button"><FileBarChart className="h-4 w-4 mr-1.5" /> Muat Data</Button>
              {profit && <Button onClick={() => profit.by_item.length ? exportProfitPDF(profit) : toast.error("Tidak ada data")} className="bg-orange-500 hover:bg-orange-600" data-testid="export-profit-report-button"><FileDown className="h-4 w-4 mr-1.5" /> Ekspor PDF</Button>}
            </div>
            {profit && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    ["Penjualan Bersih", rupiah(profit.summary.net_sales), "text-slate-900"],
                    ["HPP / Modal", rupiah(profit.summary.cogs), "text-slate-900"],
                    ["Laba Kotor", rupiah(profit.summary.gross_profit), "text-emerald-600"],
                    ["Margin", `${profit.summary.margin_pct}%`, "text-emerald-600"],
                    ["Unit Terjual", String(profit.summary.units_sold), "text-slate-900"],
                    ["Transaksi", String(profit.summary.transactions), "text-slate-900"],
                    [`Pajak ${+(((profit.summary.tax_rate ?? 0.005)) * 100).toFixed(3)}%`, rupiah(profit.summary.tax), "text-rose-600"],
                    ["Pengeluaran Kas", rupiah(profit.summary.cash_expense), "text-rose-600"],
                    ["Laba Bersih", rupiah(profit.summary.net_profit), "text-emerald-700"],
                  ].map(([l, v, c]) => (
                    <Card key={l} className="border-slate-200"><CardContent className="p-3"><div className="text-xs text-slate-500">{l}</div><div className={`text-lg font-bold mono mt-1 ${c}`}>{v}</div></CardContent></Card>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card className="border-slate-200"><CardContent className="p-4">
                    <div className="text-sm font-semibold text-slate-700 mb-3">Tren Laba Harian</div>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={profit.daily} margin={{ left: 4, right: 8, top: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tickFormatter={(d) => (d || "").slice(5)} fontSize={11} stroke="#94a3b8" />
                        <YAxis tickFormatter={(v) => (v / 1000).toLocaleString("id-ID") + "k"} fontSize={11} stroke="#94a3b8" width={48} />
                        <Tooltip formatter={(v) => rupiah(v)} labelFormatter={(d) => `Tanggal ${d}`} />
                        <Line type="monotone" dataKey="profit" name="Laba" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent></Card>
                  {!profit.limited && (
                  <Card className="border-slate-200"><CardContent className="p-4">
                    <div className="text-sm font-semibold text-slate-700 mb-3">10 Barang Paling Menguntungkan</div>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={profit.by_item.slice(0, 10)} layout="vertical" margin={{ left: 8, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                        <XAxis type="number" tickFormatter={(v) => (v / 1000).toLocaleString("id-ID") + "k"} fontSize={11} stroke="#94a3b8" />
                        <YAxis type="category" dataKey="name" width={110} fontSize={10} stroke="#94a3b8" tickFormatter={(n) => (n || "").length > 16 ? n.slice(0, 15) + "…" : n} />
                        <Tooltip formatter={(v) => rupiah(v)} />
                        <Bar dataKey="profit" name="Laba" fill="#f97316" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent></Card>
                  )}
                </div>

                {profit.limited ? null : (
                  <>
                    <div className="text-xs text-slate-500">10 barang paling menguntungkan (laba = omzet − modal; sebelum diskon nota {rupiah(profit.summary.total_discount)})</div>
                    <ReportTable head={["Barang", "Qty", "Omzet", "HPP", "Laba", "Margin"]}
                      rows={profit.by_item.slice(0, 10).map((r) => [r.name, String(r.qty), rupiah(r.revenue), rupiah(r.cost), rupiah(r.profit), `${r.margin_pct}%`])}
                      footer={`Laba kotor total: ${rupiah(profit.summary.gross_profit)} · Margin ${profit.summary.margin_pct}%`} />
                    <div className="text-xs text-slate-500">Rincian laba per kategori</div>
                    <ReportTable head={["Kategori", "Qty", "Omzet", "HPP", "Laba", "Margin"]}
                      rows={profit.by_category.map((r) => [r.category, String(r.qty), rupiah(r.revenue), rupiah(r.cost), rupiah(r.profit), `${r.margin_pct}%`])}
                      footer={`${profit.by_category.length} kategori · periode ${profit.start} s/d ${profit.end}`} />
                  </>
                )}
              </>
            )}
          </TabsContent>
        )}

        <TabsContent value="sales" className="space-y-3">
          <ReportActions onLoad={() => fetchReport("sales")} onExport={() => exp("sales")} d={data.sales} />
          {data.sales && (
            <ReportTable head={["No. Nota", "Tanggal", "Kasir", "Metode", "Total"]}
              rows={data.sales.records.map((s) => [s.number, fmtDateTime(s.created_at), s.cashier, (s.payment_method || "").toUpperCase(), rupiah(s.total)])}
              footer={`Total: ${rupiah(data.sales.total)} · ${data.sales.count} transaksi`} />
          )}
        </TabsContent>

        <TabsContent value="purchases" className="space-y-3">
          <ReportActions onLoad={() => fetchReport("purchases")} onExport={() => exp("purchases")} d={data.purchases} />
          {data.purchases && (
            <ReportTable head={["No. Pembelian", "Nota", "Supplier", "Status", "Total"]}
              rows={data.purchases.records.map((p) => [p.number, p.nota || "-", p.supplier, p.payment_status === "lunas" ? "Lunas" : "Belum Lunas", rupiah(p.total)])}
              footer={`Total: ${rupiah(data.purchases.total)}`} />
          )}
        </TabsContent>

      </Tabs>
    </div>
  );
}

function ReportActions({ onLoad, onExport, d }) {
  return (
    <div className="flex gap-2">
      <Button variant="outline" onClick={onLoad} data-testid="load-report-button"><FileBarChart className="h-4 w-4 mr-1.5" /> Muat Data</Button>
      <Button onClick={onExport} className="bg-orange-500 hover:bg-orange-600" data-testid="report-pdf-export-button"><FileDown className="h-4 w-4 mr-1.5" /> Ekspor PDF</Button>
    </div>
  );
}

function ReportTable({ head, rows, footer }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <Table>
        <TableHeader><TableRow className="bg-slate-50">{head.map((h, i) => <TableHead key={i} className={i === head.length - 1 ? "text-right" : ""}>{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {rows.map((r, i) => <TableRow key={i}>{r.map((c, j) => <TableCell key={j} className={j === r.length - 1 ? "text-right mono font-semibold" : "text-sm"}>{c}</TableCell>)}</TableRow>)}
          {rows.length === 0 && <TableRow><TableCell colSpan={head.length} className="text-center text-slate-400 py-8">Tidak ada data</TableCell></TableRow>}
        </TableBody>
      </Table>
      <div className="px-4 py-3 bg-slate-50 border-t text-sm font-medium text-slate-700">{footer}</div>
    </div>
  );
}
